import { AppGate } from "@/app/gate";
import { AppLayout } from "@/app/layout";
import type { DisplayOptions, Filter } from "@/lib/types";
import { Navigate, Outlet, createRootRoute, createRoute, createRouter, lazyRouteComponent, useParams } from "@tanstack/react-router";
// (Navigate still redirects /team/$key to its issues.)

const obj = <T,>(v: unknown): T | undefined => (v && typeof v === "object" ? (v as T) : undefined);

/** Shareable view state: filters and display options live in the URL. */
export interface ViewSearch {
  filter?: Filter;
  display?: DisplayOptions;
}
const viewSearch = (s: Record<string, unknown>): ViewSearch => ({ filter: obj<Filter>(s.filter), display: obj<DisplayOptions>(s.display) });

const root = createRootRoute({ component: () => <Outlet /> });

const login = createRoute({ getParentRoute: () => root, path: "/login", component: lazyRouteComponent(() => import("@/features/auth/login"), "LoginPage") });
const onboarding = createRoute({ getParentRoute: () => root, path: "/onboarding", component: lazyRouteComponent(() => import("@/features/auth/onboarding"), "OnboardingPage") });
const invite = createRoute({ getParentRoute: () => root, path: "/invite/$token", component: lazyRouteComponent(() => import("@/features/auth/invite"), "InvitePage") });

const app = createRoute({
  getParentRoute: () => root,
  id: "app",
  component: () => (
    <AppGate>
      <AppLayout />
    </AppGate>
  ),
});

const index = createRoute({ getParentRoute: () => app, path: "/", component: lazyRouteComponent(() => import("@/features/home/home-page"), "HomePage") });
const stickies = createRoute({ getParentRoute: () => app, path: "/stickies", component: lazyRouteComponent(() => import("@/features/stickies/stickies-page"), "StickiesPage") });
const drafts = createRoute({ getParentRoute: () => app, path: "/drafts", component: lazyRouteComponent(() => import("@/features/issues/drafts-page"), "DraftsPage") });
function TeamRedirect() {
  const { key } = useParams({ strict: false }) as { key: string };
  return <Navigate to="/team/$key/issues" params={{ key }} />;
}
const teamIndex = createRoute({ getParentRoute: () => app, path: "/team/$key", component: TeamRedirect });
const inbox = createRoute({ getParentRoute: () => app, path: "/inbox", component: lazyRouteComponent(() => import("@/features/inbox/inbox-page"), "InboxPage") });
const myIssues = createRoute({
  getParentRoute: () => app,
  path: "/my-issues",
  validateSearch: (s: Record<string, unknown>): ViewSearch & { tab?: string } => ({ ...viewSearch(s), tab: typeof s.tab === "string" ? s.tab : undefined }),
  component: lazyRouteComponent(() => import("@/features/issues/my-issues-page"), "MyIssuesPage"),
});
const teamIssues = createRoute({ getParentRoute: () => app, path: "/team/$key/issues", validateSearch: viewSearch, component: lazyRouteComponent(() => import("@/features/issues/team-issues-page"), "TeamIssuesPage") });
const triage = createRoute({ getParentRoute: () => app, path: "/team/$key/triage", component: lazyRouteComponent(() => import("@/features/issues/triage-page"), "TriagePage") });
const cycles = createRoute({ getParentRoute: () => app, path: "/team/$key/cycles", component: lazyRouteComponent(() => import("@/features/cycles/cycles-page"), "CyclesPage") });
const cycle = createRoute({ getParentRoute: () => app, path: "/team/$key/cycles/$number", validateSearch: viewSearch, component: lazyRouteComponent(() => import("@/features/cycles/cycle-page"), "CyclePage") });
const issue = createRoute({ getParentRoute: () => app, path: "/issue/$ident", component: lazyRouteComponent(() => import("@/features/issues/issue-page"), "IssuePage") });
const archive = createRoute({ getParentRoute: () => app, path: "/archive", component: lazyRouteComponent(() => import("@/features/issues/archive-page"), "ArchivePage") });
const views = createRoute({ getParentRoute: () => app, path: "/views", component: lazyRouteComponent(() => import("@/features/views/views-page"), "ViewsPage") });
const view = createRoute({ getParentRoute: () => app, path: "/view/$id", validateSearch: viewSearch, component: lazyRouteComponent(() => import("@/features/views/view-page"), "ViewPage") });
const search = createRoute({
  getParentRoute: () => app,
  path: "/search",
  validateSearch: (s: Record<string, unknown>): { q: string } => ({ q: typeof s.q === "string" ? s.q : "" }),
  component: lazyRouteComponent(() => import("@/features/search/search-page"), "SearchPage"),
});
const projects = createRoute({ getParentRoute: () => app, path: "/projects", component: lazyRouteComponent(() => import("@/features/projects/projects-page"), "ProjectsPage") });
const project = createRoute({ getParentRoute: () => app, path: "/project/$id", validateSearch: viewSearch, component: lazyRouteComponent(() => import("@/features/projects/project-page"), "ProjectPage") });
const projectTab = createRoute({ getParentRoute: () => app, path: "/project/$id/$tab", validateSearch: viewSearch, component: lazyRouteComponent(() => import("@/features/projects/project-page"), "ProjectPage") });
const initiatives = createRoute({ getParentRoute: () => app, path: "/initiatives", component: lazyRouteComponent(() => import("@/features/initiatives/initiatives-page"), "InitiativesPage") });
const initiative = createRoute({ getParentRoute: () => app, path: "/initiative/$id", component: lazyRouteComponent(() => import("@/features/initiatives/initiative-page"), "InitiativePage") });
const initiativeTab = createRoute({ getParentRoute: () => app, path: "/initiative/$id/$tab", component: lazyRouteComponent(() => import("@/features/initiatives/initiative-page"), "InitiativePage") });
const pulse = createRoute({ getParentRoute: () => app, path: "/pulse", component: lazyRouteComponent(() => import("@/features/pulse/pulse-page"), "PulsePage") });
const roadmap = createRoute({ getParentRoute: () => app, path: "/roadmap", component: lazyRouteComponent(() => import("@/features/planning/roadmap-page"), "RoadmapPage") });
const planning = createRoute({ getParentRoute: () => app, path: "/planning", component: lazyRouteComponent(() => import("@/features/planning/quarter-page"), "QuarterPlanningPage") });
const workload = createRoute({ getParentRoute: () => app, path: "/workload", component: lazyRouteComponent(() => import("@/features/planning/workload-page"), "WorkloadPage") });
const ideas = createRoute({ getParentRoute: () => app, path: "/ideas", component: lazyRouteComponent(() => import("@/features/ideas/ideas-page"), "IdeasPage") });
const idea = createRoute({ getParentRoute: () => app, path: "/idea/$id", component: lazyRouteComponent(() => import("@/features/ideas/idea-page"), "IdeaPage") });
const customers = createRoute({ getParentRoute: () => app, path: "/customers", component: lazyRouteComponent(() => import("@/features/customers/customers-page"), "CustomersPage") });
const customer = createRoute({ getParentRoute: () => app, path: "/customer/$id", component: lazyRouteComponent(() => import("@/features/customers/customer-page"), "CustomerPage") });
const docs = createRoute({ getParentRoute: () => app, path: "/docs", component: lazyRouteComponent(() => import("@/features/docs/docs-page"), "DocsPage") });
const doc = createRoute({ getParentRoute: () => app, path: "/doc/$id", component: lazyRouteComponent(() => import("@/features/docs/doc-page"), "DocPage") });
const insights = createRoute({ getParentRoute: () => app, path: "/insights", component: lazyRouteComponent(() => import("@/features/insights/insights-page"), "InsightsPage") });
const dashboards = createRoute({ getParentRoute: () => app, path: "/dashboards", component: lazyRouteComponent(() => import("@/features/insights/dashboards-page"), "DashboardsPage") });
const dashboard = createRoute({ getParentRoute: () => app, path: "/dashboard/$id", component: lazyRouteComponent(() => import("@/features/insights/dashboard-page"), "DashboardPage") });
const form = createRoute({ getParentRoute: () => app, path: "/forms/$id", component: lazyRouteComponent(() => import("@/features/forms/form-page"), "FormPage") });
const settings = createRoute({ getParentRoute: () => app, path: "/settings/$section", component: lazyRouteComponent(() => import("@/features/settings/settings-page"), "SettingsPage") });
const teamSettings = createRoute({ getParentRoute: () => app, path: "/settings/teams/$key", component: lazyRouteComponent(() => import("@/features/settings/settings-page"), "SettingsPage") });
const teamSettingsSection = createRoute({ getParentRoute: () => app, path: "/settings/teams/$key/$section", component: lazyRouteComponent(() => import("@/features/settings/settings-page"), "SettingsPage") });

const routeTree = root.addChildren([
  login,
  onboarding,
  invite,
  app.addChildren([
    index,
    stickies,
    drafts,
    teamIndex,
    inbox,
    myIssues,
    teamIssues,
    triage,
    cycles,
    cycle,
    issue,
    archive,
    views,
    view,
    search,
    projects,
    project,
    projectTab,
    initiatives,
    initiative,
    initiativeTab,
    pulse,
    roadmap,
    planning,
    workload,
    ideas,
    idea,
    customers,
    customer,
    docs,
    doc,
    insights,
    dashboards,
    dashboard,
    form,
    settings,
    teamSettings,
    teamSettingsSection,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  // Animate moving between pages, not the first load or filter changes in the URL.
  defaultViewTransition: { types: ({ fromLocation, pathChanged }) => (fromLocation && pathChanged && document.visibilityState === "visible" ? ["page"] : false) },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
